import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import LocationAndExpcept from "../../constants/LocationAndExpcept";
import { getAllLocations } from "../../utils/ApiCalls/Locations";
import {
  createMenubarSubmenu,
  updateMenubarSubmenu,
  deleteMenubarSubmenu,
  getMenubarLocationAndId,
  getMenubarSubmenuLocationAndId,
  getMenubarSubmenu,
} from "../../utils/ApiCalls/Menubar";
import { addlog } from "../../utils/ApiCalls/Logs";
import { checkFields } from "../../utils/Validation/checkFields";

const MenubarMenuItemInput = ({ menubarId, parentUrl, submenu }) => {
  const navigate = useNavigate();
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [locationOption, setLocationOption] = useState([]);
  const [displayLocation, setDisplayLocation] = useState([]);
  const [currentLocation, setCurrentLocation] = useState([]);
  const [menuName, setMenuName] = useState(submenu?.menuName || "");
  const [simplifiedChineseMenuName, setSimplifiedChineseMenuName] = useState(
    submenu?.simplifiedChineseMenuName || ""
  );
  const [traditionalChineseMenuName, setTraditionalChineseMenuName] = useState(
    submenu?.traditionalChineseMenuName || ""
  );
  const [bahasaMenuName, setBahasaMenuName] = useState(submenu?.bahasaMenuName || "");
  const [url, setUrl] = useState(submenu?.url || "");
  const [isActive, setIsActive] = useState(
    submenu?.is_active === undefined ? true : submenu.is_active === 1
  );
  const [submitBtnState, setSubmitBtnState] = useState(false);

  useEffect(() => {
    getOptionLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOptionLocation = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    let data = await getAllLocations();
    if (data.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      setLocationOption(data?.data || []);
      if (!submenu?.id) {
        const singapore = (data?.data || []).find(
          (item) => item.location_code === "SG"
        );
        if (singapore) setDisplayLocation([singapore.id]);
      }
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    if (submenu) {
      setMenuName(submenu.menuName || "");
      setSimplifiedChineseMenuName(submenu.simplifiedChineseMenuName || "");
      setTraditionalChineseMenuName(submenu.traditionalChineseMenuName || "");
      setBahasaMenuName(submenu.bahasaMenuName || "");
      setUrl(submenu.url || "");
      if (submenu.is_active !== undefined) {
        setIsActive(submenu.is_active === 1);
      }
      if (submenu.id) getSubmenuLocationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submenu]);

  const getSubmenuLocationData = async () => {
    let data = await getMenubarSubmenuLocationAndId(submenu.url ?? "");
    if (data.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      setCurrentLocation(data || []);
      setDisplayLocation((data || []).map((item) => item.locationId));
    }
  };

  const handleDisplayLocationChange = (selectedOptions) => {
    setDisplayLocation(selectedOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const values = {
      menuName,
      simplifiedChineseMenuName,
      traditionalChineseMenuName,
      bahasaMenuName,
      url,
      is_active: isActive ? 1 : 0,
    };

    let valid = checkFields(values, "menubarSubmenu", [
      "menuName",
      "simplifiedChineseMenuName",
      "traditionalChineseMenuName",
      "bahasaMenuName",
      "url",
    ]);
    if (!valid && displayLocation.length === 0) {
      valid = "Error: The following fields are empty - location";
    }

    if (!valid) {
      const menuList = await getMenubarSubmenu(menubarId);
      if (!menuList?.error) {
        const isDuplicate = (menuList || []).some(
          (item) => item.url === url && item.id !== submenu?.id
        );
        if (isDuplicate) {
          valid = "Error: This URL/Slug is already present";
        }
      }
    }

    if (valid) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: valid,
      });
      return;
    }

    setSubmitBtnState(true);
    dispatch({ type: "SET_LOADING", loading: true });
    try {
      const parentByLocation = await getMenubarLocationAndId(parentUrl ?? "");
      if (parentByLocation.error) throw new Error(parentByLocation.error);

      await Promise.all(
        currentLocation.map(async (item) => {
          if (!displayLocation.includes(item.locationId)) {
            await deleteMenubarSubmenu(item.menubarId, item.id);
          }
        })
      );

      const skippedLocations = [];
      await Promise.all(
        displayLocation.map(async (locationId) => {
          const existing = currentLocation.find(
            (item) => item.locationId === locationId
          );
          if (existing) {
            const data = await updateMenubarSubmenu(
              existing.menubarId,
              existing.id,
              values
            );
            if (data?.error) throw new Error(data.error);
            return;
          }
          const parent = parentByLocation.find(
            (item) => item.locationId === locationId
          );
          if (!parent) {
            skippedLocations.push(locationId);
            return;
          }
          const data = await createMenubarSubmenu(parent.id, values);
          if (data?.error) throw new Error(data.error);
        })
      );

      addlog(
        submenu?.id ? "Menubar Sub-menu Updated" : "Menubar Sub-menu Created",
        "Menubar",
        { id: submenu?.id, menuName, url, locations: displayLocation },
        userDetails.id
      );

      if (skippedLocations.length > 0) {
        const names = skippedLocations
          .map((id) => locationOption.find((l) => l.id === id)?.location_name)
          .filter(Boolean)
          .join(", ");
        Swal.fire({
          icon: "warning",
          title: "Some locations skipped",
          text: `Parent menu not found for ${names}. Create the parent menu first, then try adding the sub-menu again.`,
        });
      }

      navigate(`/website/cms/homepage/menubar/${menubarId}/view`);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.message || "Unknown error occurred",
      });
    } finally {
      setSubmitBtnState(false);
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <form className="input_form" onSubmit={handleSubmit}>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={menuName}
                    onChange={(e) => setMenuName(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Simplified Chinese Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={simplifiedChineseMenuName}
                    onChange={(e) => setSimplifiedChineseMenuName(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Traditional Chinese Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={traditionalChineseMenuName}
                    onChange={(e) => setTraditionalChineseMenuName(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Bahasa Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={bahasaMenuName}
                    onChange={(e) => setBahasaMenuName(e.target.value)}
                  />
                </div>
              </div>
              <LocationAndExpcept
                nondisplay
                displayLocation={displayLocation}
                locationOption={locationOption}
                setDisplayLocation={setDisplayLocation}
                handleDisplayLocationChange={handleDisplayLocationChange}
              />
              <div className="w-100"></div>
              <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    URL / Slug <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    placeholder="e.g. deals/cash-vouchers"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="iti_inner">
                  <div className="icheck-primary d-inline">
                    <input
                      type="checkbox"
                      id="menubarSubmenuActiveCheck"
                      checked={isActive}
                      onChange={() => setIsActive(!isActive)}
                    />
                    <label htmlFor="menubarSubmenuActiveCheck">
                      <span style={{ fontWeight: isActive ? 600 : 200 }}>
                        Active
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card-footer text-right">
            <button disabled={submitBtnState} className="btn main_submit">
              {submenu?.id ? "Update" : "Add"} Sub-menu
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenubarMenuItemInput;
